import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConversionCta from "@/components/ConversionCta";
import { usePageMeta } from "@/hooks/usePageMeta";
import { pageBySlug, relatedLaunchPages } from "@/seo/launchPages";

export default function GuidePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = pageBySlug(slug);
  const live = page && page.status === "launch";
  usePageMeta(live ? page : {
    path: `/balatro/${slug}`,
    title: "Guide not published yet | GamePlat",
    description: "This Balatro guide isn't live yet. Browse the Balatro hub for guides that are.",
    indexable: false,
  });

  if (!live) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-28 text-center">
        <p className="font-vt323 text-2xl text-gp-gold tracking-widest">404</p>
        <h1 className="font-unbounded font-black tracking-tighter text-4xl mt-3">That guide is not live yet</h1>
        <p className="text-white/60 mt-4">Yorick is holding queued questions until Search Console demand and a review. The launch set is on the Balatro hub.</p>
        <Button onClick={() => navigate("/balatro")} className="mt-8 rounded-full bg-gp-blue">Balatro hub</Button>
      </div>
    );
  }

  const related = relatedLaunchPages(page);

  return (
    <article className="mx-auto max-w-3xl px-5 md:px-8 py-12 md:py-16" data-testid="guide-page">
      <p className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">{page.cluster} · {page.query}</p>
      <h1 className="font-unbounded font-black tracking-tighter text-4xl sm:text-5xl mt-2">{page.h1}</h1>
      <p className="text-white/75 text-lg mt-5 leading-relaxed">{page.answer}</p>
      {page.bullets?.length > 0 && (
        <ul className="mt-8 space-y-3">
          {page.bullets.map((item) => (
            <li key={item} className="glass rounded-2xl px-5 py-4 text-white/80 leading-relaxed">{item}</li>
          ))}
        </ul>
      )}
      {page.faq?.length > 0 && (
        <div className="mt-10 space-y-4">
          <h2 className="font-unbounded font-bold text-xl">Quick answers</h2>
          {page.faq.map((item) => (
            <div key={item.q} className="rounded-2xl border border-white/10 bg-black/20 px-5 py-4">
              <h3 className="font-semibold">{item.q}</h3>
              <p className="text-sm text-white/65 mt-2 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      )}
      {related.length > 0 && (
        <div className="mt-10">
          <h2 className="font-unbounded font-bold text-xl flex items-center gap-2"><BookOpen size={18} className="text-gp-blue" /> Keep going</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {related.map((item) => (
              <Link key={item.path} to={item.path} className="glass rounded-2xl px-4 py-3 hover:bg-white/5 transition-colors">
                <span className="text-xs uppercase tracking-widest text-white/40">{item.cluster}</span>
                <p className="font-semibold mt-1">{item.h1}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="mt-8 flex flex-wrap gap-3">
        <Button onClick={() => navigate("/chat?game=balatro")} variant="outline" className="rounded-full border-white/15 bg-white/5">
          Ask Yorick this question <ArrowRight size={16} className="ml-2" />
        </Button>
        <Link to="/balatro" className="text-white/60 hover:text-white self-center text-sm">Back to Balatro hub</Link>
      </div>
      <ConversionCta source={page.slug} />
    </article>
  );
}
